import Pagination from "../Pagination";
import TableBody from "./TableBody";
import TableHeader from "./TableHeader";
import { TTableProps } from "../../types/TableView";
import Search from "../Search";
import PerPage from "../PerPage";
import Card from "../Card";
import * as XLSX from "xlsx";
import { useState } from "react";
import { useLocation } from "react-router-dom";

const TableView = ({
  searchValue,
  searchLoading,
  rows,
  columns,
  searchPlaceholder,
  showPagination,
  showSearch,
  showPerPage,
  onPageChange,
  handleSearchParams,
  onPerPageChange,
  isLoading,
  isMounted,
  loadingMessage,
  searchComponent,
  paginationData,
  filters,
  showFilter,
  cardHeaderStyle,
  selectedRows,
  showSearchButton = false,
  data
}: TTableProps) => {
 const { currentPage, totalPage, perPage, totalRecords: totalCount } = (paginationData || {
  currentPage: 0,
  totalPage: 0,
  perPage: 0,
  totalRecords: 0,
}) as { currentPage: number; totalPage: number; perPage: number; totalRecords: number };

  const [isExporting, setIsExporting] = useState(false);
  const location = useLocation();
  // Calculate records count for current page
 const calculateRecordsCount = () => {
  const totalRecords = paginationData?.totalCount
  // const totalRecords = (totalPage > 0 && perPage > 0) ? totalPage * perPage : rows?.length || 0;
  const startRecord = totalRecords > 0 ? ((currentPage - 1) * perPage) + 1 : 0;
  const endRecord = Math.min(currentPage * perPage, totalRecords);
  
  return { startRecord, endRecord, totalRecords };
};

const { startRecord, endRecord, totalRecords } = calculateRecordsCount();

  const handleClick = () => {
    if (!selectedRows || selectedRows.length === 0 || !data) return;

    setIsExporting(true);

    try {
      const filteredData = data.filter((item: any) => selectedRows.includes(item._id));

      
      const fixedAttributeKeys = [
        "AC adapter power",
        "Charging port type",
        "USB-C PD",
        "USB-C Voltage",
        "AC input voltage",
        "Legacy AC adapter tip size",
        "Legacy Power Tip",
        "Charging Protocols",
        "Display diagonal",
        "Display resolution",
        "ASPECT RATIO",
        "Touchscreen",
        "DisplayPorts quantity",
        "HDMI ports quantity",
        "DVI port",
        "Cable lock slot",
        "Cable lock slot type (1)",
        "Cable lock slot type (2)",
        "Universal",
        "Number of USB-A Ports",
        "USB 3.2 GEN 2 (3.1 GEN 2) TYPE-A PORTS QUANTITY",
        "USB-A Upstream Communication Protocols",
        "USB 3.2 Gen 1 (3.1 Gen 1) Type-A ports quantity ",
        "USB 2.0 Type-A Ports Quantity",
        "USB 2.0 ports quantity",
        "Number of USB-C Ports",
        "USB 2.0 Type-C Ports",
        "USB 3.2 GEN 1 (3.1 GEN 1) TYPE-C PORTS QUANTITY",
        "USB-C Upstream Communication Protocols",
        "USB 3.2 Gen 2 (3.1 Gen 2) Type-C ports quantity",
        "DisplayPort Alt Mode Type-C Ports",
        "Thunderbolt 3 Type-C Ports",
        "Thunderbolt 4 Type-C Ports",
        "USB4 Type-C ports",
        "Thunderbolt 5 Type C Ports",
        "Combo headphone/mic port",
        "Ethernet LAN (RJ-45) ports",
        "Wi-Fi",
        "Wi-Fi standards",
        "Bluetooth",
        "Width",
        "Depth",
        "Height",
        "Weight",
        "Form factor",
        "Front camera",
        "Front camera resolution",
        "Height (rear)",
        "AC input frequency",
        "Height (front)",
      ];

      // const sanitize = (str: string) => (str || "").replace(/\r?\n|\r/g, " ").replace(/"/g, '""');
      const sanitize = (str: string) => (str || "").replace(/\r?\n|\r/g, " ");
      const rows:any = filteredData.map((item: any) => {
        const isProduct = location.pathname === "/product";
        const isAccessory = location.pathname === "/accessory";

        const row: any = isProduct
          ? {
            ProductId:sanitize(item._id),
            Regions: sanitize(item.regions?.map((r: any) => r.regionName).join("; ") || ""),
            ModelNumber: sanitize(item.modelNumber || ""),
            Description: sanitize(item.description || item.displayName),
            Model: sanitize(item.model || ""),
            Brand: sanitize(item.brand || ""),
            DisplayName: sanitize(item.displayName || ""),
            Master: sanitize(item.Master ? "YES" : "NO"),
            
          }
          : isAccessory
            ? {
              AccessoryId: sanitize(item._id),
              Regions: sanitize(item.regions?.map((r: any) => r.regionName).join("; ") || ""),
              ModelNumber: sanitize(item.modelNumber || ""),
              Category: sanitize(item.category?.categoryName || ""),
              Subcategory: sanitize(item.subcategory?.subcategoryName || ""),
              accessoryName: sanitize(item.accessoryName || ""),
              Description: sanitize(item.description || ""),
              Brand: sanitize(item.brand || ""),
              Model: sanitize(item.model || ""),
              Sku: sanitize(item.sku || ""),
              CompatibilityNotes: sanitize(item.compatibilityNotes?.map((note: any) => note.name).join(', ') || '')
            }
            : {};

        if (isProduct) {
          fixedAttributeKeys.forEach((attrName) => {
            const attr = item.attributes?.find((a: any) => a.attributeName === attrName);
            row[attrName] = sanitize(attr ? attr.value || "" : "");
          });

          // Handle combined unit columns
          const widthAttr = item.attributes?.find((a: any) => a.attributeName === "Width");
          const depthAttr = item.attributes?.find((a: any) => a.attributeName === "Depth");
          const heightAttr = item.attributes?.find((a: any) => a.attributeName === "Height");
          const heightFrontAttr = item.attributes?.find((a: any) => a.attributeName === "Height (front)");
          const heightRearAttr = item.attributes?.find((a: any) => a.attributeName === "Height (rear)");
          const legacyTipAttr = item.attributes?.find((a: any) => a.attributeName === "Legacy AC adapter tip size");
          const weightAttr = item.attributes?.find((a: any) => a.attributeName === "Weight");

          // Combined unit for Width, Depth, Height
          const dimensionUnit = widthAttr?.unit?.name || depthAttr?.unit?.name || heightAttr?.unit?.name || "";
          row["Width, Depth, Height (Unit)"] = sanitize(dimensionUnit);

          // Combined unit for Height front/rear
          const heightFrontRearUnit = heightFrontAttr?.unit?.name || heightRearAttr?.unit?.name || "";
          row["Height front/rear (Unit)"] = sanitize(heightFrontRearUnit);

          // Individual units for Legacy AC adapter tip size and Weight
          row["Legacy AC adapter tip size (Unit)"] = sanitize(legacyTipAttr?.unit?.name || "");
          row["Weight (Unit)"] = sanitize(weightAttr?.unit?.name || "");

        } else if (isAccessory) {
          item.attributes?.forEach((attr: any) => {
            row[attr.attributeName] = sanitize(attr.value || "");
          });
        }
        return row;
      });


  
     
      const isProduct = location.pathname === "/product";
      const attributeGroups: any = isProduct ? {
        'Power': [
          'AC adapter power',
          'Charging port type',
          'USB-C PD',
          'USB-C Voltage',
          'AC input voltage',
          'Legacy AC adapter tip size', 'Legacy AC adapter tip size (Unit)',
          'Legacy Power Tip',
          'Charging Protocols'
        ],
        'Display': [
          'Display diagonal',
          'Display resolution',
          'ASPECT RATIO',
          'Touchscreen'
        ],
        'Video Ports': [
          'DisplayPorts quantity',
          'HDMI ports quantity',
          'DVI port'
        ],
        'Cable Locks': [
          'Cable lock slot',
          'Cable lock slot type (1)',
          'Cable lock slot type (2)',
          'Universal'
        ],
        'Type-A ports': [
          'Number of USB-A Ports',
          'USB 3.2 GEN 2 (3.1 GEN 2) TYPE-A PORTS QUANTITY',
          'USB 3.2 Gen 1 (3.1 Gen 1) Type-A ports quantity ',
          'USB 2.0 Type-A Ports Quantity',
          'USB 2.0 ports quantity',
          'USB-A Upstream Communication Protocols'
        ],
        'Type-C Ports': [
          'Number of USB-C Ports',
          'USB 2.0 Type-C Ports',
          'USB 3.2 GEN 1 (3.1 GEN 1) TYPE-C PORTS QUANTITY',
          'USB 3.2 Gen 2 (3.1 Gen 2) Type-C ports quantity',
          'DisplayPort Alt Mode Type-C Ports',
          'Thunderbolt 3 Type-C Ports',
          'Thunderbolt 4 Type-C Ports',
          'USB4 Type-C ports',
          'Thunderbolt 5 Type C Ports',
          'USB-C Upstream Communication Protocols'
        ],
        'Audio Ports': [
          'Combo headphone/mic port'
        ],
        'Communication': [
          'Ethernet LAN (RJ-45) ports',
          'Wi-Fi',
          'Wi-Fi standards',
          'Bluetooth'
        ],
        'Dimensions': [
          'Width',
          'Depth', 
          'Height',
          'Width, Depth, Height (Unit)', // Unit column immediately after W/D/H
          'Weight', 
          'Weight (Unit)' // Unit column immediately after Weight
        ],
        'Others': [
          'Form factor',
          'Front camera',
          'Front camera resolution',
          'Height (rear)',
          'Height (front)',
          'Height front/rear (Unit)', // Unit column immediately after Height front/rear
          'AC input frequency'
        ]
      } : null; // No groups for accessories

      // Replace the sheetData creation with this conditional logic
      let sheetData;

      if (isProduct) {
        // For products - use manual column ordering
        const orderedHeaders = [
          // Basic product info
          'ProductId', 'Regions', 'ModelNumber', 'Description', 'Model', 'Brand', 'DisplayName', 'Master',
          // Power group
          'AC adapter power', 'Charging port type', 'USB-C PD', 'USB-C Voltage', 'AC input voltage',
          'Legacy AC adapter tip size', 'Legacy AC adapter tip size (Unit)', 'Legacy Power Tip', 'Charging Protocols',
          // Display group
          'Display diagonal', 'Display resolution', 'ASPECT RATIO', 'Touchscreen',
          // Video Ports group
          'DisplayPorts quantity', 'HDMI ports quantity', 'DVI port',
          // Cable Locks group
          'Cable lock slot', 'Cable lock slot type (1)', 'Cable lock slot type (2)', 'Universal',
          // Type-A ports group
          'Number of USB-A Ports', 'USB 3.2 GEN 2 (3.1 GEN 2) TYPE-A PORTS QUANTITY',
          'USB 3.2 Gen 1 (3.1 Gen 1) Type-A ports quantity ', 'USB 2.0 Type-A Ports Quantity', 'USB 2.0 ports quantity', 'USB-A Upstream Communication Protocols',
          // Type-C Ports group
          'Number of USB-C Ports', 'USB 2.0 Type-C Ports', 'USB 3.2 GEN 1 (3.1 GEN 1) TYPE-C PORTS QUANTITY',
          'USB 3.2 Gen 2 (3.1 Gen 2) Type-C ports quantity', 'DisplayPort Alt Mode Type-C Ports',
          'Thunderbolt 3 Type-C Ports', 'Thunderbolt 4 Type-C Ports', 'USB4 Type-C ports', 'Thunderbolt 5 Type C Ports', 'USB-C Upstream Communication Protocols',
          // Audio Ports group
          'Combo headphone/mic port',
          // Communication group
          'Ethernet LAN (RJ-45) ports', 'Wi-Fi', 'Wi-Fi standards', 'Bluetooth',
          // Dimensions group
          'Width', 'Depth', 'Height', 'Width, Depth, Height (Unit)', 'Weight', 'Weight (Unit)',
          // Others group
          'Form factor', 'Front camera', 'Front camera resolution', 'Height (rear)', 'AC input frequency', 'Height (front)',
          'Height front/rear (Unit)', 
        ];

        // Filter to only include headers that exist in the data
        const headers = orderedHeaders.filter(header => rows[0] && rows[0].hasOwnProperty(header));
        
        const groupHeaderRow: string[] = headers.map((key) => {
          if (attributeGroups) {
            for (const groupName in attributeGroups) {
              if (attributeGroups[groupName].includes(key)) return groupName;
            }
          }
          return ""; // Non-grouped columns
        });

        const attributeHeaderRow = headers;

        // NEW ROW: Add custom data for each column based on provided specification
        const customData = "GAF generated / EUROPE, ASIA-PACIFIC, UNITED KINGDOM, CANADA ,AUSTRALIA & NEW ZEALAND ,UNITED STATES / M4Z01PA#abg (HP product or part number) ,21MC or 21MC006PFJ (Lenovo Type or Machine or Type or Model (MTM) number) ,Surface_Go_2_1926 (Microsoft System SKU), NX.MJNSA.001 (Acer part number)  Not used for master model or generic model listing for entire model family  / description Or displayName / Model ,family,series and model number like Aspire and E1-572G-74504G1TDnkk /  brand/ GAF generated from Model with (Model Number) appended. /GAF Generated / number (in watts)like, 65 blank/ Barrel DC-in-jack USB-C USB microB blank/ Yes No blank/ number like 19.5 blank /number like 19.5 blank /number like 4.5 blank /millimeter inch blank /Tip name like 3H1 or 3H2 3H2 3W blank /  Yes No blank /Number (incehs only) like 15.6 blank /resolution like 1366 x 768/ ratio like 16:9 (not decimal)/ Yes No blank /quantity blank/ quantity blank /quantity blank /quantity blank/ lock type like, Kensington® T-bar™ Kensington® Nano Noble® WEDGELOCK® Surface Mount lock slot (if no lock slot) blank /lock type like, Kensington® T-bar™ Kensington® Nano Noble® WEDGELOCK® blank /Yes (if locktype is unknown) blank /quantity blank/quantity blank/quantity blank/ quantity blank /quantity blank /quantity blank /quantity blank /quantity blank /quantity blank /quantity blank /quantity blank /quantity blank /quantity blank/ quantity blank /quantity blank / quantity blank /  quantity blank /quantity blank/ Yes No blank /like 802.11 a or b or n blank /Yes No blank/ number like 317.2 blank /number like 285.6 blank /number like 19.5 blank/ millimeter centimeter meter inch yard blank /number like 1.5 blank /grams kilograms pounds blank /Like clam shell 2-in-1 tablet blank /Yes No blank /Like 5MP blank /number like 19.5 blank /like 60 Hz blank /number like 19.5 blank /millimeter centimeter meter inch blank";

        const customDataArray = customData.split('/').map(item => item.trim());

        // Create new row by mapping each header to corresponding custom data
        const newRow = headers.map((header, index) => {
          return customDataArray[index] || "blank";
        });

        // Data rows
        const dataRows = rows.map((row: { [x: string]: any; }) => headers.map((h) => row[h]));

        // Insert group header as the first row, attribute names as second row, new row as third row
        sheetData = [groupHeaderRow, attributeHeaderRow, newRow, ...dataRows];
      } else {
  // For accessories - collect ONLY unique attributes from SELECTED accessories
  const allAttributeNames = new Set();
  
  // First add the basic accessory fields
  const basicFields = ['AccessoryId','Regions', 'ModelNumber', 'Category', 'Subcategory', 'accessoryName', 'Description', 'Brand', 'Model', 'Sku' , 'CompatibilityNotes'];
  basicFields.forEach(field => allAttributeNames.add(field));
  
  // Then collect unique attribute names ONLY from selected/filtered accessories
  filteredData.forEach((item: any) => {
    if (item.attributes && Array.isArray(item.attributes)) {
      item.attributes.forEach((attr: any) => {
        if (attr.attributeName) {
          allAttributeNames.add(attr.attributeName);
        }
      });
    }
  });
  
  // Convert Set to Array for consistent ordering
  const headers = Array.from(allAttributeNames);
  
  // Create rows with all columns (empty string for missing attributes)
  const accessoryRows = filteredData.map((item: any) => {
    const row: any = {
      AccessoryId: sanitize(item._id),
      Regions: sanitize(item.regions?.map((r: any) => r.regionName).join("; ") || ""),
      ModelNumber: sanitize(item.modelNumber || ""),
      Category: sanitize(item.category?.categoryName || ""),
      Subcategory: sanitize(item.subcategory?.subcategoryName || ""),
      accessoryName: sanitize(item.accessoryName || ""),
      Description: sanitize(item.description || ""),
      Brand: sanitize(item.brand || ""),
      Model: sanitize(item.model || ""),
      Sku: sanitize(item.sku || ""),
      CompatibilityNotes: sanitize(item.compatibilityNotes?.map((note: any) => note.name).join(', ') || '')
    };

    // Add all attributes from current item
    if (item.attributes && Array.isArray(item.attributes)) {
      item.attributes.forEach((attr: any) => {
        if (attr.attributeName) {
          row[attr.attributeName] = sanitize(attr.value || "");
        }
      });
    }
    
    // Fill missing attributes with empty strings to maintain column consistency
    headers.forEach((header: any) => {
      if (!row.hasOwnProperty(header)) {
        row[header] = "";
      }
    });
    
    return row;
  });

  const attributeHeaderRow = headers;
  const dataRows = accessoryRows.map((row: any) => headers.map((h: any) => row[h]));

  sheetData = [attributeHeaderRow, ...dataRows];
}

      // Generate worksheet from this 2D array
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

      // Make the merges conditional
      if (isProduct && attributeGroups) {
        // Apply merges only for products
        const merges: XLSX.Range[] = [];
        let lastGroup = "";
        let startIndex = 0;

        // Use the same ordered headers for merging
        const orderedHeaders = [
          'ProductId', 'Regions', 'ModelNumber', 'Description', 'Model', 'Brand', 'DisplayName', 'Master',
          'AC adapter power', 'Charging port type', 'USB-C PD', 'USB-C Voltage', 'AC input voltage',
          'Legacy AC adapter tip size', 'Legacy AC adapter tip size (Unit)', 'Legacy Power Tip', 'Charging Protocols', 
          'Display diagonal', 'Display resolution', 'ASPECT RATIO', 'Touchscreen',
          'DisplayPorts quantity', 'HDMI ports quantity', 'DVI port',
          'Cable lock slot', 'Cable lock slot type (1)', 'Cable lock slot type (2)', 'Universal',
          'Number of USB-A Ports', 'USB 3.2 GEN 2 (3.1 GEN 2) TYPE-A PORTS QUANTITY',
          'USB 3.2 Gen 1 (3.1 Gen 1) Type-A ports quantity ', 'USB-A Upstream Communication Protocols', 'USB 2.0 Type-A Ports Quantity', 'USB 2.0 ports quantity',
          'Number of USB-C Ports', 'USB 2.0 Type-C Ports', 'USB-C Upstream Communication Protocols', 'USB 3.2 GEN 1 (3.1 GEN 1) TYPE-C PORTS QUANTITY',
          'USB 3.2 Gen 2 (3.1 Gen 2) Type-C ports quantity', 'DisplayPort Alt Mode Type-C Ports',
          'Thunderbolt 3 Type-C Ports', 'Thunderbolt 4 Type-C Ports', 'USB4 Type-C ports', 'Thunderbolt 5 Type C Ports',
          'Combo headphone/mic port',
          'Ethernet LAN (RJ-45) ports', 'Wi-Fi', 'Wi-Fi standards', 'Bluetooth',
          'Width', 'Depth', 'Height', 'Width, Depth, Height (Unit)', 'Weight', 'Weight (Unit)',
          'Form factor', 'Front camera', 'Front camera resolution', 'Height (rear)', 'Height (front)',
          'Height front/rear (Unit)', 'AC input frequency'
        ];

        const headers = orderedHeaders.filter(header => rows[0] && rows[0].hasOwnProperty(header));
        
        headers.forEach((key: string, index: number) => {
          const group = (() => {
            for (const g in attributeGroups) {
              if (attributeGroups[g].includes(key)) return g;
            }
            return "";
          })();

          if (group !== lastGroup) {
            if (lastGroup && index - startIndex > 1) {
              merges.push({
                s: { r: 0, c: startIndex },
                e: { r: 0, c: index - 1 }
              });
            }
            startIndex = index;
            lastGroup = group;
          }

          // Last one
          if (index === headers.length - 1 && group && index - startIndex >= 1) {
            merges.push({
              s: { r: 0, c: startIndex },
              e: { r: 0, c: index }
            });
          }
        });

        worksheet["!merges"] = merges;
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, location.pathname === "/accessory" ? "Accessory" : "Products");

      const now = new Date();
      const filename =
        location.pathname === "/accessory"
          ? `Accessory_export_${now.toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`
          : location.pathname === "/product"
            ? `Product_export_${now.toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`
            : `Export_${now.toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`;

      XLSX.writeFile(workbook, filename);

      //const worksheet = XLSX.utils.json_to_sheet(rows);
      /* const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, window.location.pathname === "/accessory" ? "Accessory" : "Products");

      const csvOutput = XLSX.write(workbook, {
        bookType: "csv",
        type: "string",
        FS: ",",
        RS: "\n",
        strip: true,
        blankrows: false,
      } as any);

      const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const now = new Date();
      const filename = location.pathname === "/accessory" ? `Accessory_export_${now.toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv` : location.pathname === "/product" ? `Product_export_${now.toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv` : ``;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link); */

    } finally {
      setIsExporting(false);
    }
  };

  
const itemType = (() => {
  const route = location.pathname.split('/')[1] || '';
  const capitalized = route.charAt(0).toUpperCase() + route.slice(1);
  if (route.endsWith('s') || route.endsWith('ies')) {
    return capitalized;
  }
  return route.endsWith('y') 
    ? route.charAt(0).toUpperCase() + route.slice(1, -1) + 'ies'
    : capitalized + 's';
})();
  return (
    <Card>
      <div className="card-body p-0">
        <div className="table-responsive">
          {showSearch && (
            <div
              className="card-header border-0 ps-0 pe-0 align-items-center"
              style={
                cardHeaderStyle
                  ? { paddingTop: cardHeaderStyle }
                  : { paddingTop: "0" }
              }
            >
              <div className="card-title">
                <div className="d-flex align-items-center justify-content-between position-relative my-1">
                  <Search
                    placeholder={searchPlaceholder || ""}
                    handleSearchParams={handleSearchParams as any}
                    showSearchButton={showSearchButton}
                    value={searchValue || ""}
                  />
                </div>
              </div>
              {(window.location.pathname === "/product" || window.location.pathname === "/accessory") && selectedRows && selectedRows.length >= 1 && (
                <button
                  className="btn btn-light-primary my-1 me-2"
                  onClick={handleClick}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-export"></i> Export CSV
                    </>
                  )}
                </button>
              )}

              <div className="d-flex gap-2">{showFilter && filters}</div>
            </div>
          )}
          <table className="table align-middle table-striped table-hover gs-0 gy-5">
            <TableHeader columns={columns} />
            <TableBody
              searchLoading={searchLoading}
              rows={rows}
              columns={columns}
              isSearching={isLoading}
              isMounted={isMounted}
              searchComponent={searchComponent}
            />
          </table>
          <div className="card-footer py-5 px-0 d-flex  justify-content-between">
            {showPerPage && (
              <div className="w d-flex align-items-center ">
                <PerPage perPage={perPage} onPerPageChange={onPerPageChange} />
              </div>
            )}
            <div>
              {/* Records Count Display */}
              {showPagination && totalRecords !== undefined && (
                <div className="fs-5 fw-800 text-black-950 text-center py-3 position-relative overflow-hidden">
                  {isLoading || searchLoading ? (
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <div className="spinner-grow spinner-grow-sm text-primary" role="status" style={{ animationDuration: '0.8s' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="spinner-grow spinner-grow-sm text-primary" role="status" style={{ animationDuration: '1s', animationDelay: '0.2s' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="spinner-grow spinner-grow-sm text-primary" role="status" style={{ animationDuration: '0.8s', animationDelay: '0.4s' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="ms-2 text-muted" style={{ letterSpacing: '0.5px' }}>
                        Loading {itemType}...
                      </span>
                    </div>
                  ) : (
                    <div className="d-inline-flex align-items-center gap-3 px-4 py-2 rounded-pill bg-light border border-2">
                      <span style={{ letterSpacing: '0.3px' }}>
                        Showing <span className="text-primary fw-bold">{startRecord}</span> to{' '}
                        <span className="text-primary fw-bold">{endRecord}</span> of{' '}
                        <span className="text-success fw-bold">{totalRecords}</span> {itemType}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {showPagination && (
              <Pagination
                currentPage={currentPage}
                totalPage={totalPage}
                onPageChange={onPageChange as Function}
              />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TableView;
